import { TimeRange, sharedKeyUtils } from './shared-utils'

// Transcription query keys with hierarchical structure
export const transcriptionKeys = {
  // Base key for all transcription-related queries
  all: ['transcriptions'] as const,
  
  // Individual transcription queries
  details: () => [...transcriptionKeys.all, 'detail'] as const,
  detail: (id: string) => [...transcriptionKeys.details(), id] as const,
  
  // Video-specific transcription queries
  video: () => [...transcriptionKeys.all, 'video'] as const,
  videoTranscription: (videoId: string) => [...transcriptionKeys.video(), videoId] as const,
  videoTranscriptionStatus: (videoId: string) => [
    ...transcriptionKeys.video(), 
    videoId, 
    'status'
  ] as const,
  videoTranscriptionData: (videoId: string) => [
    ...transcriptionKeys.video(), 
    videoId, 
    'data'
  ] as const,
  
  // User transcription queries
  user: () => [...transcriptionKeys.all, 'user'] as const,
  userTranscriptions: (userId: string, filters?: TranscriptionFilters) => [
    ...transcriptionKeys.user(), 
    userId, 
    { filters }
  ] as const,
  userTranscriptionStats: (userId: string) => [
    ...transcriptionKeys.user(), 
    userId, 
    'stats'
  ] as const,
  
  // Processing and status queries
  processing: () => [...transcriptionKeys.all, 'processing'] as const,
  processingQueue: () => [...transcriptionKeys.processing(), 'queue'] as const,
  processingStatus: (jobId: string) => [...transcriptionKeys.processing(), jobId] as const,
  processingProgress: (jobId: string) => [
    ...transcriptionKeys.processing(), 
    jobId, 
    'progress'
  ] as const,
  
  // Search and analysis
  search: () => [...transcriptionKeys.all, 'search'] as const,
  searchTranscriptions: (query: string, filters?: TranscriptionFilters) => [
    ...transcriptionKeys.search(), 
    { query, filters }
  ] as const,
  
  // Analytics and insights
  analytics: () => [...transcriptionKeys.all, 'analytics'] as const,
  wordClouds: (userId?: string, timeRange?: TimeRange) => [
    ...transcriptionKeys.analytics(), 
    'wordclouds', 
    { userId, timeRange }
  ] as const,
  sentimentAnalysis: (transcriptionId: string) => [
    ...transcriptionKeys.analytics(), 
    'sentiment', 
    transcriptionId
  ] as const,
  topicAnalysis: (transcriptionId: string) => [
    ...transcriptionKeys.analytics(), 
    'topics', 
    transcriptionId
  ] as const,
  
  // Admin queries
  admin: () => [...transcriptionKeys.all, 'admin'] as const,
  adminStats: () => [...transcriptionKeys.admin(), 'stats'] as const,
  adminQueue: () => [...transcriptionKeys.admin(), 'queue'] as const,
  adminUsage: (timeRange: TimeRange) => [
    ...transcriptionKeys.admin(), 
    'usage', 
    { timeRange }
  ] as const,
} as const

// Transcription-specific types and interfaces
export interface TranscriptionFilters {
  status?: TranscriptionStatus
  language?: string
  confidence?: 'low' | 'medium' | 'high'
  sortBy?: 'createdAt' | 'updatedAt' | 'confidence' | 'duration'
  sortOrder?: 'asc' | 'desc'
  search?: string
  dateFrom?: string
  dateTo?: string
  hasTimestamps?: boolean
  minDuration?: number
  maxDuration?: number
  limit?: number
  page?: number
}

export type TranscriptionStatus = 
  | 'pending' 
  | 'processing' 
  | 'completed' 
  | 'failed' 
  | 'cancelled'

export type TranscriptionLanguage = 
  | 'en' 
  | 'es' 
  | 'fr' 
  | 'de' 
  | 'it' 
  | 'pt' 
  | 'ru' 
  | 'ja' 
  | 'ko' 
  | 'zh'

export interface TranscriptionSegment {
  id: string
  text: string
  startTime: number
  endTime: number
  confidence: number
  speaker?: string
}

export interface TranscriptionData {
  id: string
  videoId: string
  userId: string
  status: TranscriptionStatus
  language: TranscriptionLanguage
  confidence: number
  duration: number
  segments: TranscriptionSegment[]
  fullText: string
  createdAt: string
  updatedAt: string
  processedAt?: string
}

export interface TranscriptionJob {
  id: string
  videoId: string
  userId: string
  status: TranscriptionStatus
  progress: number
  estimatedTimeRemaining?: number
  startedAt: string
  updatedAt: string
  error?: string
}

export interface TranscriptionStats {
  totalTranscriptions: number
  completedTranscriptions: number
  processingTranscriptions: number
  failedTranscriptions: number
  totalDuration: number
  averageConfidence: number
  languageDistribution: Record<TranscriptionLanguage, number>
}

// Type for all possible transcription query keys
export type TranscriptionQueryKey = typeof transcriptionKeys[keyof typeof transcriptionKeys]

// Utility functions for transcription keys
export const transcriptionKeyUtils = {
  // Check if a key is for a specific video
  isVideoTranscriptionKey: (key: readonly unknown[]): boolean => {
    return key.includes('transcriptions') && key.includes('video')
  },
  
  // Check if a key is for user transcriptions
  isUserTranscriptionKey: (key: readonly unknown[]): boolean => {
    return key.includes('transcriptions') && key.includes('user')
  },
  
  // Check if a key is for processing/job status
  isProcessingKey: (key: readonly unknown[]): boolean => {
    return key.includes('transcriptions') && key.includes('processing')
  },
  
  // Check if a key is for analytics
  isAnalyticsKey: (key: readonly unknown[]): boolean => {
    return key.includes('transcriptions') && key.includes('analytics')
  },
  
  // Extract video ID from transcription key
  getVideoIdFromKey: (key: readonly unknown[]): string | null => {
    return sharedKeyUtils.extractIdFromKey(key, 'video', [])
  },
  
  // Extract user ID from transcription key
  getUserIdFromKey: (key: readonly unknown[]): string | null => {
    return sharedKeyUtils.extractIdFromKey(key, 'user', ['stats'])
  },
  
  // Extract job ID from processing key
  getJobIdFromKey: (key: readonly unknown[]): string | null => {
    return sharedKeyUtils.extractIdFromKey(key, 'processing', ['queue'])
  },
  
  // Extract transcription ID from analytics key
  getTranscriptionIdFromKey: (key: readonly unknown[]): string | null => {
    const analyticsIndex = key.indexOf('analytics')
    if (analyticsIndex !== -1 && analyticsIndex + 2 < key.length) {
      const transcriptionId = key[analyticsIndex + 2]
      return typeof transcriptionId === 'string' ? transcriptionId : null
    }
    return null
  },
  
  // Use shared utilities
  ...sharedKeyUtils,
}

export default transcriptionKeys